import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { IAuditLog, IAuditLogFilters } from "../../../@types/AuditLog";
import { TableSorted } from "../../../components/TableSorted";
import { useAuditLogs } from "../../../hooks/queries/admin/auditLogs";
import { useModal } from "../../../hooks/useModal";
import { AUDIT_LOG_PAGE_SIZE_OPTIONS } from "../../../services/admin/auditLogService";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  AuditLogDetailModal,
  PageContainer,
  PageHeader,
  PageTitle,
  TableContainer,
  TablePagination,
  TableWrapper,
  LoadingOverlay,
  ErrorMessage,
} from "../shared";
import { CHANGE_OPERATIONS } from "../shared/AuditLogPanel/auditLogWindow";
import { AuditLogFilters } from "./components/AuditLogFilters";

const DEFAULT_PAGE_SIZE = 10;

export function AdminAuditLogs() {
  document.title = "Audit logs - Admin";

  const { openModal, closeModal } = useModal();

  const [filters, setFilters] = useState<IAuditLogFilters>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    // Matches the filters' "Hide views", ticked by default.
    operations: CHANGE_OPERATIONS,
    startDate: dayjs().subtract(6, "day").startOf("day").toISOString(),
    endDate: dayjs().endOf("day").toISOString(),
  });

  // TanStack Query refetches automatically when the `filters` change because they're
  // part of the query key (see useAuditLogs). No manual refetch effect needed.
  const { auditLogs, pagination, isLoading, isFetching, isError, error } =
    useAuditLogs(filters);

  const handleApplyFilters = (newFilters: Partial<IAuditLogFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setFilters((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  const handleRowClick = (auditLog: IAuditLog) => {
    openModal(
      // The list leaves the before/after snapshots out (EN-5288); the modal fetches them.
      <AuditLogDetailModal
        auditLog={auditLog}
        loadDetails
        onClose={closeModal}
      />,
      {
        width: "min(900px, 92vw)",
        maxHeight: "90vh",
        showCloseButton: true,
      }
    );
  };

  const columns: ColumnDef<IAuditLog, string>[] = useMemo(
    () => [
      {
        accessorKey: "timestamp",
        header: "Date",
        cell: ({ row }) => (
          <span>
            {row.original.timestamp
              ? getLongFormattedDate(row.original.timestamp)
              : "-"}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "tableName",
        header: "Table name",
        cell: ({ row }) => <span>{row.original.tableName}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "operation",
        header: "Operation",
        cell: ({ row }) => <span>{row.original.operation}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "applicationUserEmail",
        header: "User",
        cell: ({ row }) => (
          <span>{row.original.applicationUserEmail || "-"}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => <span>{row.original.source || "-"}</span>,
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Audit logs</PageTitle>
      </PageHeader>

      <AuditLogFilters
        onApplyFilters={handleApplyFilters}
        isLoading={isLoading || isFetching}
      />

      {!isLoading && isError && (
        <ErrorMessage>
          {serverErrorMessage(error) ??
            "Failed to load audit logs. Please try again."}
        </ErrorMessage>
      )}

      <TableContainer>
        {isLoading && <LoadingOverlay>Loading audit logs...</LoadingOverlay>}

        {!isLoading && !isError && (
          <>
            <TableWrapper refreshKeys={[["admin-audit-logs"]]}>
              <TableSorted
                columns={columns}
                data={auditLogs}
                onRowClick={handleRowClick}
                emptyStateText="No audit logs found for the selected filters"
              />
            </TableWrapper>

            <TablePagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={AUDIT_LOG_PAGE_SIZE_OPTIONS}
              exportCsv={{
                filename: "audit-logs",
                columns,
                rows: auditLogs,
                currentPageOnly: true,
              }}
            />
          </>
        )}
      </TableContainer>
    </PageContainer>
  );
}
